/**
 * Display adapters: GraphQL `Attorney` and nested `Expert` on `MatterExpert`
 * (see MatterFields fragment) → connect `User` for UI that still expects that shape.
 *
 * Prefer nested `expert` on each row; `resolveExpertById` matches
 * `buildGetExpertByIdFromMatters` for expert ids that appear on retainers without
 * a populated row on this matter.
 */
import type {
  MatterExpertRow,
  MatterViewModel,
} from '~/modules/matters/matters.types';
import { UserRole, type User } from '~/shared/types/connect';

export function attorneyUserFromMatterGraph(
  attorney: MatterViewModel['attorney'] | null | undefined,
): User | null {
  if (!attorney) {
    return null;
  }
  return {
    id: attorney.id,
    name: attorney.name,
    email: attorney.email,
    role: UserRole.ATTORNEY,
  };
}

export function expertUserFromMatterExpertRow(
  matterExpert: MatterExpertRow,
  resolveExpertById: (id: string) => User | undefined,
): User | null {
  const nested = matterExpert.expert;
  if (nested) {
    return {
      id: nested.id,
      name: nested.name,
      email: nested.email,
      role: UserRole.EXPERT,
      avatarUrl: nested.avatarUrl ?? undefined,
    };
  }
  return resolveExpertById(matterExpert.expertId) ?? null;
}

export function expertUserForMatterExpertId(
  matter: MatterViewModel,
  expertId: string,
  resolveExpertById: (id: string) => User | undefined,
): User | undefined {
  const row = matter.matterExperts.find(
    (matterExpert) => matterExpert.expertId === expertId,
  );
  if (row) {
    return expertUserFromMatterExpertRow(row, resolveExpertById) ?? undefined;
  }
  return resolveExpertById(expertId);
}
